import { useEffect, useMemo, useState } from 'react';
import { Panel, PanelType } from '@fluentui/react';
import {
  Button,
  Checkbox,
  FluentProvider,
  Link,
  makeStyles,
  Spinner,
  Text,
  tokens,
  webLightTheme,
} from '@fluentui/react-components';
import {
  cloneTenant,
  CLONE_TENANT_OPTIONS,
  CloneTenantOption,
  ICloneTenantRequest,
} from '../../services/tenantService';
import { getEntitiesMetadatas } from '../../services/metadataService';

interface ICloneTenantPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  tenantId: number;
  tenantName?: string;
  onCloned: () => void;
}

const ENTITY_METADATA_TYPES = [
  { type: 'Asset', label: 'Assets' },
  { type: 'Order', label: 'Orders' },
  { type: 'Work', label: 'Works' },
  { type: 'Contractor', label: 'Contractor' },
] as const;

type CheckState = boolean | 'mixed';

type CheckNode = {
  id: string;
  label: string;
  customizable?: boolean;
  children?: CheckNode[];
};

const optionId = (value: CloneTenantOption) => `opt:${value}`;
const typeId = (type: string) => `type:${type}`;
const nameId = (type: string, name: string) => `${type}|${name}`;
const META_ID = optionId(CloneTenantOption.EntityMetadata);

const collectLeaves = (node: CheckNode): string[] =>
  node.children?.length ? node.children.flatMap(collectLeaves) : [node.id];

const checkState = (node: CheckNode, selected: Set<string>): CheckState => {
  const leaves = collectLeaves(node);
  const n = leaves.reduce((count, id) => count + (selected.has(id) ? 1 : 0), 0);
  if (n === 0) return false;
  if (n === leaves.length) return true;
  return 'mixed';
};

const toggleLeaves = (selected: Set<string>, node: CheckNode): Set<string> => {
  const leaves = collectLeaves(node);
  const next = new Set(selected);
  const allOn = leaves.every((id) => next.has(id));
  for (const id of leaves) {
    if (allOn) next.delete(id);
    else next.add(id);
  }
  return next;
};

const toggleId = (ids: Set<string>, id: string): Set<string> => {
  const next = new Set(ids);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

const parseMetadataKey = (key: string) => {
  const sep = key.indexOf('|');
  if (sep <= 0) return undefined;
  const type = key.slice(0, sep).trim();
  const name = key.slice(sep + 1).trim();
  if (!type || !name) return undefined;
  return { type, name };
};

const groupMetadataNames = (keys: string[]) => {
  const byType: Record<string, string[]> = {};
  for (const key of keys) {
    const parsed = parseMetadataKey(key);
    if (!parsed) continue;
    if (!byType[parsed.type]) byType[parsed.type] = [];
    if (!byType[parsed.type].includes(parsed.name)) {
      byType[parsed.type].push(parsed.name);
    }
  }
  for (const type of Object.keys(byType)) {
    byType[type].sort((a, b) => a.localeCompare(b));
  }
  return byType;
};

const buildCloneTree = (namesByType?: Record<string, string[]>): CheckNode => ({
  id: 'all',
  label: 'Select All',
  children: CLONE_TENANT_OPTIONS.map((option) => {
    if (option.value !== CloneTenantOption.EntityMetadata) {
      return { id: optionId(option.value), label: option.label };
    }
    return {
      id: META_ID,
      label: option.label,
      customizable: true,
      children: ENTITY_METADATA_TYPES.map((item) => ({
        id: typeId(item.type),
        label: item.label,
        customizable: true,
        children: (namesByType?.[item.type] ?? []).map((name) => ({
          id: nameId(item.type, name),
          label: name,
        })),
      })),
    };
  }),
});

const allLeaves = (namesByType?: Record<string, string[]>) =>
  new Set(collectLeaves(buildCloneTree(namesByType)));

const hydrateMetaLeaves = (
  selected: Set<string>,
  namesByType: Record<string, string[]>
): Set<string> => {
  const next = new Set(selected);
  for (const { type } of ENTITY_METADATA_TYPES) {
    const names = namesByType[type] ?? [];
    if (names.length === 0 || !next.has(typeId(type))) continue;
    next.delete(typeId(type));
    for (const name of names) next.add(nameId(type, name));
  }
  return next;
};

const toCloneRequest = (
  selected: Set<string>,
  tree: CheckNode
): ICloneTenantRequest => {
  const options: number[] = [];
  for (const option of CLONE_TENANT_OPTIONS) {
    if (option.value === CloneTenantOption.EntityMetadata) continue;
    if (selected.has(optionId(option.value))) options.push(option.value);
  }
  const meta = tree.children?.find((node) => node.id === META_ID);
  if (!meta) return { options };
  const leaves = collectLeaves(meta);
  const picked = leaves.filter((id) => selected.has(id));
  if (picked.length === 0) return { options };
  options.push(CloneTenantOption.EntityMetadata);
  if (picked.length === leaves.length) return { options };
  return {
    options,
    entityMetadataSelection: {
      values: picked.filter((id) => id.includes('|')),
    },
  };
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingTop: '8px',
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  nested: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingLeft: '24px',
  },
  footer: {
    display: 'flex',
    gap: '8px',
    paddingTop: '16px',
  },
});

const CheckRow = ({
  label,
  checked,
  onToggle,
  customizable,
  onCustomize,
  busy,
  accent,
}: {
  label: string;
  checked: CheckState;
  onToggle: () => void;
  customizable?: boolean;
  onCustomize?: () => void;
  busy?: boolean;
  accent?: boolean;
}) => {
  const styles = useStyles();
  return (
    <div className={styles.optionRow}>
      <Checkbox
        label={
          accent ? (
            <Text weight="bold" style={{ color: tokens.colorBrandForeground1 }}>
              {label}
            </Text>
          ) : (
            label
          )
        }
        checked={checked}
        onChange={onToggle}
      />
      {customizable && (
        <Link as="button" onClick={onCustomize}>
          Customize
        </Link>
      )}
      {busy && <Spinner size="tiny" />}
    </div>
  );
};

const CheckBranch = ({
  node,
  selected,
  expanded,
  onToggle,
  onExpand,
  loading,
  busy,
}: {
  node: CheckNode;
  selected: Set<string>;
  expanded: Set<string>;
  onToggle: (node: CheckNode) => void;
  onExpand: (id: string) => void;
  loading?: boolean;
  busy?: boolean;
}) => {
  const styles = useStyles();
  const kids = node.children ?? [];
  const open = expanded.has(node.id);

  return (
    <div>
      <CheckRow
        label={node.label}
        checked={checkState(node, selected)}
        onToggle={() => onToggle(node)}
        customizable={node.customizable}
        onCustomize={() => onExpand(node.id)}
        busy={busy}
      />
      {open && node.customizable && (
        <div className={styles.nested}>
          {kids.length === 0 ? (
            <Text size={200}>{loading ? 'Loading…' : 'No metadata found'}</Text>
          ) : (
            kids.map((child) => (
              <CheckBranch
                key={child.id}
                node={child}
                selected={selected}
                expanded={expanded}
                onToggle={onToggle}
                onExpand={onExpand}
                loading={loading}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const CloneTenantPanel = ({
  isOpen,
  onDismiss,
  tenantId,
  tenantName,
  onCloned,
}: ICloneTenantPanelProps) => {
  const styles = useStyles();
  const [selected, setSelected] = useState(() => allLeaves());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [namesByType, setNamesByType] = useState<
    Record<string, string[]> | undefined
  >();
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  const tree = useMemo(() => buildCloneTree(namesByType), [namesByType]);
  const request = useMemo(
    () => toCloneRequest(selected, tree),
    [selected, tree]
  );
  const metaOpen = expanded.has(META_ID);

  useEffect(() => {
    if (!isOpen || !metaOpen || namesByType) return;
    const abortController = new AbortController();
    setLoadingMeta(true);
    getEntitiesMetadatas(
      abortController,
      ENTITY_METADATA_TYPES.map((item) => item.type)
    )
      .then((resp) => {
        if (abortController.signal.aborted) return;
        const grouped = groupMetadataNames(Object.keys(resp?.metadatas ?? {}));
        setNamesByType(grouped);
        setSelected((prev) => hydrateMetaLeaves(prev, grouped));
      })
      .catch(() => {
        if (!abortController.signal.aborted) setNamesByType({});
      })
      .finally(() => {
        if (!abortController.signal.aborted) setLoadingMeta(false);
      });
    return () => abortController.abort();
  }, [isOpen, metaOpen, namesByType]);

  const toggle = (node: CheckNode) =>
    setSelected((prev) => toggleLeaves(prev, node));

  const expand = (id: string) => setExpanded((prev) => toggleId(prev, id));

  const reset = () => {
    setSelected(allLeaves());
    setExpanded(new Set());
    setNamesByType(undefined);
  };

  const handleDismiss = () => {
    reset();
    onDismiss();
  };

  const handleClone = async () => {
    setIsCloning(true);
    const abortController = new AbortController();
    try {
      const response = await cloneTenant(abortController, tenantId, request);
      if (response.isSuccess) {
        onCloned();
        handleDismiss();
      }
    } catch {
      // error handled by global error bar
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={handleDismiss}
      closeButtonAriaLabel="Close"
      type={PanelType.smallFixedFar}
      headerText={`Clone Tenant${tenantName ? `: ${tenantName}` : ''}`}
      styles={{ content: { paddingTop: 20 } }}
    >
      <FluentProvider theme={webLightTheme}>
        <div className={styles.container}>
          <Text size={300}>Select what to clone from this tenant:</Text>

          <div className={styles.checkboxGroup}>
            <CheckRow
              label={tree.label}
              checked={checkState(tree, selected)}
              onToggle={() => toggle(tree)}
              accent
            />
            {tree.children?.map((node) => (
              <CheckBranch
                key={node.id}
                node={node}
                selected={selected}
                expanded={expanded}
                onToggle={toggle}
                onExpand={expand}
                loading={loadingMeta}
                busy={node.id === META_ID && loadingMeta}
              />
            ))}
          </div>

          <div className={styles.footer}>
            <Button
              appearance="primary"
              onClick={handleClone}
              disabled={
                isCloning || request.options.length === 0 || loadingMeta
              }
              icon={isCloning ? <Spinner size="tiny" /> : undefined}
            >
              {isCloning ? 'Cloning...' : 'Clone'}
            </Button>
            <Button appearance="secondary" onClick={handleDismiss}>
              Cancel
            </Button>
          </div>
        </div>
      </FluentProvider>
    </Panel>
  );
};

export default CloneTenantPanel;
